import {BarEvent, BarEventStatus, UptimeBar, UptimeBarPillSize} from "@uptime-com/uptimebar";
import {differenceInHours} from "date-fns";
import {useMemo} from "react";

import {useCurrentOrPublicUserContext} from "@/components/context/hooks.ts";
import {useMediaQuery} from "@/lib/hooks/useMediaQuery.ts";
import {CheckDetailsAlert, CheckStateEventWindow, LocationResponseTimes} from "@/lib/queries/checks.ts";
import {UptimeBarNewToolTipRenderer} from "@/pages/checks/components/details/UptimeBar/subComponents/UptimeBarPillToolTip/UptimeBarPillToolTip.tsx";
import "@uptime-com/uptimebar/styles";

import {getEventsFromDataSources} from "@/pages/checks/components/details/UptimeBar/utils/UptimeBarEventsUtils.ts";
import {mapUptimeBarEvents} from "@/pages/checks/components/details/UptimeBar/utils/UptimeBarWindowMapper.ts";

interface UptimeBarCheckAnalysisProps {
  start: Date;
  end: Date;
  createdAt: Date;
  interval: number;
  responseTimes: LocationResponseTimes[];
  intervalAlerts: CheckDetailsAlert[];
  noResponseTimesDefaultStatus?: BarEventStatus;
  size?: UptimeBarPillSize;
  uptimeBarEvents: CheckStateEventWindow[];
}

export const UptimeBarCheckAnalysis = ({
  start,
  end,
  responseTimes,
  intervalAlerts,
  createdAt,
  noResponseTimesDefaultStatus = BarEventStatus.NO_DATA,
  size,
  uptimeBarEvents,
}: UptimeBarCheckAnalysisProps) => {
  const isMobileSmall = useMediaQuery(`(max-width: 770px)`);
  const isMobileTiny = useMediaQuery(`(max-width: 440px)`);
  const isMobile = useMediaQuery(`(max-width: 768px)`);

  const {timezone, currentDateFormat, currentTimeFormat} = useCurrentOrPublicUserContext();

  const hours = differenceInHours(end, start);

  const events: BarEvent[] = useMemo((): BarEvent[] => {
    if (uptimeBarEvents && uptimeBarEvents.length > 0) {
      // For check types like CloudStatus and Webhook, the backend reports NO_DATA when no incidents
      // exist. Since "no data" means "service is up" for these types, remap to the desired status
      // so the bar renders green instead of grey.
      const noDataOverride =
        noResponseTimesDefaultStatus !== BarEventStatus.NO_DATA ? noResponseTimesDefaultStatus : undefined;
      return mapUptimeBarEvents(uptimeBarEvents, noDataOverride);
    }

    const defaultInterval = hours > 24 ? 60 * 24 : 60;
    return getEventsFromDataSources({
      start,
      end,
      createdAt,
      interval: defaultInterval, // I think this should be interval from props but it acts odd sometimes
      responseTimes,
      intervalAlerts,
      timezone,
      noResponseTimesDefaultStatus,
    });
  }, [
    hours,
    start,
    end,
    createdAt,
    responseTimes,
    intervalAlerts,
    noResponseTimesDefaultStatus,
    timezone,
    uptimeBarEvents,
  ]);

  const toolTipRender = UptimeBarNewToolTipRenderer(timezone, currentDateFormat, currentTimeFormat);

  return (
    <UptimeBar
      start={start}
      end={end}
      timezone={timezone}
      events={events}
      defaultMissingEventStatus={noResponseTimesDefaultStatus}
      size={size ?? (isMobileTiny ? "small" : "large")}
      maxNumberOfLabels={isMobileTiny ? 3 : isMobileSmall ? 5 : 7}
      toolTip={toolTipRender}
      toolTipProps={{boundary: document.querySelector(".outlet-container") ?? undefined}}
      toolTipContentProps={{
        className: "gap-1",
        style: {
          width: isMobile ? "200px" : "",
        },
      }}
    />
  );
};
